import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';

import {
  StepData,
  StepIndicatorComponent,
} from '../../shared/step-indicator/step-indicator';
import {
  TipoProveedorOAuthEnum,
  TIPO_PROVEEDOR_OAUTH_NAME,
} from '../../../constants/tipo-proveedor-oauth.enum';
import {
  ConfirmInformationState,
  LoginState,
} from '../../../types/login.types';
import { LoginService } from '../../../services/login.service';
import { LogoHeaderComponent } from '../shared/logo-header/logo-header.component';

@Component({
  selector: 'app-confirm-information',
  imports: [
    CommonModule,
    FormsModule,
    StepIndicatorComponent,
    FloatLabelModule,
    InputTextModule,
    LogoHeaderComponent,
  ],
  templateUrl: './confirm-information.component.html',
})
export class ConfirmInformationComponent implements OnInit, OnDestroy {
  currentState: ConfirmInformationState | undefined | null;

  readonly TIPO_PROVEEDOR_OAUTH = TipoProveedorOAuthEnum;
  readonly TIPO_PROVEEDOR_OAUTH_NAME = TIPO_PROVEEDOR_OAUTH_NAME;
  stepData: StepData = {
    currentStep: 1,
    totalSteps: 2,
    currentStepLabel: 'Confirma tu Información',
    nextStepLabel: 'Valida tu teléfono',
  };

  name: string = '';
  email: string = '';
  password: string = '';
  showPassword: boolean = false;

  nameError: {
    message: string;
  } | null = null;

  emailError: {
    message: string;
  } | null = null;
  passwordError: {
    message: string;
  } | null = null;

  constructor(private loginService: LoginService) {
    this.loginService.loginState$.subscribe((state) => {
      if (state.type === LoginState.confirmInformation) {
        this.currentState = state as ConfirmInformationState;
        this.name = this.currentState?.loginFlow.name || '';
        this.email = this.currentState?.loginFlow.email || '';
      }
    });
  }

  ngOnInit(): void {}

  goBack(): void {
    this.loginService.notifyLoginState({
      type: LoginState.login,
      email: this.currentState?.loginFlow.email || '',
      timestamp: new Date(),
    });
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  continuar(): void {
    if (!this.validateForm()) return;

    this.loginService.notifyLoginState({
      type: LoginState.phoneVerification,
      timestamp: new Date(),
      loginFlow: {
        name: this.name,
        email: this.currentState?.loginFlow.email || '',
        idProveedor:
          this.currentState?.loginFlow.idProveedor ||
          TipoProveedorOAuthEnum.CREDENCIAL,
        idToken: this.currentState?.loginFlow.idToken || '',
        password: this.password,
      },
    });
  }

  private resetErrors(): void {
    this.clearNameError();
    this.clearEmailError();
    this.clearPasswordError();
  }

  clearNameError(): void {
    this.nameError = null;
  }

  clearEmailError(): void {
    this.emailError = null;
  }

  clearPasswordError(): void {
    this.passwordError = null;
  }

  private validateName(): boolean {
    if (this.name.trim() === '') {
      this.nameError = {
        message: 'Por favor ingresa tu nombre',
      };
      return false;
    }

    const nameLength = this.name.trim()?.length || 0;
    if (nameLength < 2) {
      this.nameError = {
        message: 'El nombre debe tener al menos 2 caracteres',
      };
      return false;
    }

    return true;
  }

  private validateEmail(): boolean {
    if (
      this.currentState?.loginFlow.idProveedor !==
      TipoProveedorOAuthEnum.CREDENCIAL
    ) {
      return true;
    }

    if (this.email.trim() === '') {
      this.emailError = {
        message: 'Por favor ingresa tu correo electrónico',
      };
      return false;
    }

    if (!this.isValidEmail(this.email || '')) {
      this.emailError = {
        message: 'Por favor ingresa un correo electrónico válido',
      };
      return false;
    }

    return true;
  }

  private validatePasswordIfNeeded(): boolean {
    if (
      this.currentState?.loginFlow?.idProveedor !==
      TipoProveedorOAuthEnum.CREDENCIAL
    ) {
      return true;
    }

    if (this.password.trim() === '') {
      this.passwordError = {
        message: 'Por favor ingresa tu contraseña',
      };
      return false;
    }

    if (!this.isValidPassword(this.password)) {
      this.passwordError = {
        message: 'La contraseña no cumple con todos los requisitos',
      };
      return false;
    }

    return true;
  }

  isValidPassword(password: string): boolean {
    return password.length >= 6;
  }

  isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  validateForm(): boolean {
    this.resetErrors();
    let isValid = true;

    isValid = this.validateName() && isValid;
    isValid = this.validateEmail() && isValid;
    isValid = this.validatePasswordIfNeeded() && isValid;

    return isValid;
  }

  getInputClasses(hasError: boolean, isPassword: boolean = false): string {
    const baseClasses =
      'w-full px-4 py-4 xl:py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent text-gray-700 bg-gray-50';
    const errorClasses = 'border-2 border-red-500 focus:ring-red-500';
    const normalClasses = 'border border-gray-300 focus:ring-appPrimary-500';
    const passwordPadding = isPassword ? 'pr-12' : '';

    return `${baseClasses} ${passwordPadding} ${
      hasError ? errorClasses : normalClasses
    }`;
  }

  getPhoneInputClasses(hasError: boolean): string {
    const baseClasses =
      'flex-1 px-4 py-4 xl:py-2.5 border rounded-r-lg focus:outline-none focus:ring-2 focus:border-transparent text-gray-700';
    const errorClasses = 'border-2 border-red-500 focus:ring-red-500';
    const normalClasses = 'border border-gray-300 focus:ring-appPrimary-500';

    return `${baseClasses} ${hasError ? errorClasses : normalClasses}`;
  }

  getButtonClasses(isDisabled: boolean): string {
    const baseClasses =
      'w-full py-2.5 rounded-lg font-medium text-base transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2';

    if (isDisabled) {
      return `${baseClasses} bg-gray-400 text-white cursor-not-allowed flex items-center justify-center`;
    }

    return `${baseClasses} bg-allasRed text-white hover:bg-allasRed-600 focus:ring-appPrimary-500`;
  }

  ngOnDestroy(): void {}
}
